import {
  ArrowRight,
  Building2,
  Check,
  FileText,
  Loader2,
  Sparkles,
  Users,
  Video,
} from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import {
  normalizeMeetingDocStatus,
} from "../../../../services/meetingDocsHelpers";
import type { MeetingDoc } from "../../../../services/meetingDocsEndpoint";
import type { MeetingRowData } from "../meetingsTypes";
import { MEETING_GROUP_ORDER } from "../meetingsTypes";

type MeetingsListTableProps = {
  meetings: MeetingRowData[];
  onMeetingAction?: (meeting: MeetingRowData) => void;
  onDocOpen?: (meeting: MeetingRowData & { docOverride?: MeetingDoc }) => void;
  onMeetingClick?: (meeting: MeetingRowData) => void;
  preparingMeetingIds?: ReadonlySet<string>;
  followingUpMeetingIds?: ReadonlySet<string>;
};

const ActionButton = ({
  meeting,
  onMeetingAction,
  preparingMeetingIds,
  followingUpMeetingIds,
}: {
  meeting: MeetingRowData;
  onMeetingAction?: (meeting: MeetingRowData) => void;
  preparingMeetingIds?: ReadonlySet<string>;
  followingUpMeetingIds?: ReadonlySet<string>;
}) => {
  if (!meeting.action) return <span className="text-sm text-muted-foreground">&mdash;</span>;

  const actionType = meeting.action.type;
  const isPrep = actionType === "prep";
  const isFollowUp = actionType === "follow_up";

  if (actionType === "followed_up") {
    return (
      <Badge variant="outline" className="gap-1.5 border-border-chip bg-muted font-normal text-muted-foreground">
        <Check className="h-3 w-3" />
        {meeting.action.label}
      </Badge>
    );
  }

  const relevantStatus = isFollowUp ? meeting.followupStatus : meeting.prepStatus;
  const isPreparing = Boolean(isPrep && (meeting.prepStatus === "pending" || meeting.prepStatus === "running" || preparingMeetingIds?.has(meeting.id)));
  const isFollowingUp = Boolean(isFollowUp && (meeting.followupStatus === "pending" || meeting.followupStatus === "running" || followingUpMeetingIds?.has(meeting.id)));
  const isLoading = isPreparing || isFollowingUp;
  const isFailed = relevantStatus === "failed";
  const isClickable = (isPrep || isFollowUp) && typeof onMeetingAction === "function";

  const loadingLabel = isFollowUp ? "Following up..." : "Preparing...";
  const failedLabel = isFollowUp ? "Retry follow-up" : "Retry prep";

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 gap-1.5 border-border-chip bg-accent-tint text-xs font-normal text-primary hover:bg-accent-tint/80"
      onClick={isClickable ? (e: React.MouseEvent) => { e.stopPropagation(); onMeetingAction(meeting); } : undefined}
      disabled={!isClickable || isLoading}
      aria-label={
        isPrep
          ? `Prep for meeting ${meeting.title}`
          : `Follow up on meeting ${meeting.title}`
      }
    >
      {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
      {isLoading ? loadingLabel : (isFailed ? failedLabel : meeting.action.label)}
      <ArrowRight className="h-3 w-3" />
    </Button>
  );
};

const DocChips = ({
  meeting,
  onDocOpen,
}: {
  meeting: MeetingRowData;
  onDocOpen?: (meeting: MeetingRowData & { docOverride?: MeetingDoc }) => void;
}) => {
  const prepReady = meeting.prepDoc && normalizeMeetingDocStatus(meeting.prepDoc.status) === "success";
  const followupReady = meeting.followupDoc && normalizeMeetingDocStatus(meeting.followupDoc.status) === "success";

  if (!prepReady && !followupReady) return null;

  return (
    <div className="mt-1.5 flex items-center gap-1.5">
      {prepReady && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
          onClick={(e) => { e.stopPropagation(); onDocOpen?.({ ...meeting, docOverride: meeting.prepDoc }); }}
        >
          <Sparkles className="h-3 w-3" /> Prep Brief
        </button>
      )}
      {followupReady && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
          onClick={(e) => { e.stopPropagation(); onDocOpen?.({ ...meeting, docOverride: meeting.followupDoc }); }}
        >
          <Sparkles className="h-3 w-3" /> Follow-up Brief
        </button>
      )}
    </div>
  );
};

const MeetingRow = ({
  meeting,
  onMeetingAction,
  onDocOpen,
  onMeetingClick,
  preparingMeetingIds,
  followingUpMeetingIds,
}: {
  meeting: MeetingRowData;
  onMeetingAction?: (meeting: MeetingRowData) => void;
  onDocOpen?: (meeting: MeetingRowData & { docOverride?: MeetingDoc }) => void;
  onMeetingClick?: (meeting: MeetingRowData) => void;
  preparingMeetingIds?: ReadonlySet<string>;
  followingUpMeetingIds?: ReadonlySet<string>;
}) => {
  const additionalCount = Math.max(0, meeting.attendees.length - 1);
  const companyDomain = meeting.company?.domain || (meeting.company?.name ? getDomainFromCompany(meeting.company.name) : "");

  return (
    <div
      className="grid grid-cols-[100px_1fr_160px_180px_200px_80px] items-center gap-4 border-b border-border px-6 py-3 transition-colors hover:bg-muted cursor-pointer"
      onClick={() => onMeetingClick?.(meeting)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onMeetingClick?.(meeting); }}
    >
      <div className="text-sm font-medium text-muted-foreground">{meeting.timeLabel}</div>

      <div className="min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">{meeting.title}</span>
        <DocChips meeting={meeting} onDocOpen={onDocOpen} />
      </div>

      <div className="flex min-w-0 items-center gap-2">
        {meeting.company ? (
          <>
            <CompanyLogo
              domain={companyDomain}
              name={meeting.company.name}
              className="h-5 w-5 shrink-0"
            />
            <span className="truncate text-sm text-foreground">{meeting.company.name}</span>
          </>
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4 shrink-0" />
            No company
          </span>
        )}
      </div>

      <div className="flex items-center">
        <ActionButton
          meeting={meeting}
          onMeetingAction={onMeetingAction}
          preparingMeetingIds={preparingMeetingIds}
          followingUpMeetingIds={followingUpMeetingIds}
        />
      </div>

      <div className="flex min-w-0 items-center gap-2">
        {meeting.attendees.length > 0 ? (
          <>
            <div className="flex -space-x-1.5">
              {meeting.attendees.slice(0, 3).map((attendee) => (
                <div
                  key={`${meeting.id}-${attendee.name}`}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-muted-foreground text-[10px] font-medium text-white ring-2 ring-background"
                  title={attendee.name}
                >
                  {attendee.initials}
                </div>
              ))}
            </div>
            <span className="truncate text-sm text-foreground">{meeting.attendees[0].name}</span>
            {additionalCount > 0 && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                +{additionalCount}
              </span>
            )}
          </>
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Users className="h-4 w-4" />
            No attendees
          </span>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        {meeting.hasRecording && <Video className="h-4 w-4 text-muted-foreground" />}
        {meeting.hasNotes && <FileText className="h-4 w-4 text-muted-foreground" />}
      </div>
    </div>
  );
};

const MeetingsListTable = ({ meetings, onMeetingAction, onDocOpen, onMeetingClick, preparingMeetingIds, followingUpMeetingIds }: MeetingsListTableProps) => (
  <div className="flex-1 overflow-auto">
    <div className="sticky top-0 z-10 grid grid-cols-[100px_1fr_160px_180px_200px_80px] items-center gap-4 border-b border-border bg-muted px-6 py-2">
      <div className="text-xs font-medium text-muted-foreground">Time</div>
      <div className="text-xs font-medium text-muted-foreground">Meeting</div>
      <div className="text-xs font-medium text-muted-foreground">Company</div>
      <div className="text-xs font-medium text-muted-foreground">AI Action</div>
      <div className="text-xs font-medium text-muted-foreground">Attendees</div>
      <div className="text-right text-xs font-medium text-muted-foreground">Media</div>
    </div>

    {MEETING_GROUP_ORDER.map((groupLabel) => {
      const groupMeetings = meetings.filter((meeting) => meeting.group === groupLabel);
      if (!groupMeetings.length) return null;

      return (
        <div key={groupLabel}>
          <div className="flex items-center justify-between border-b border-border bg-muted px-6 py-2.5">
            <span className="text-xs font-medium text-foreground">{groupLabel}</span>
            <span className="text-xs text-muted-foreground">
              {groupMeetings.length}
              {" "}
              meeting{groupMeetings.length !== 1 ? "s" : ""}
            </span>
          </div>

          {groupMeetings.map((meeting) => (
            <MeetingRow
              key={meeting.id}
              meeting={meeting}
              onMeetingAction={onMeetingAction}
              onDocOpen={onDocOpen}
              onMeetingClick={onMeetingClick}
              preparingMeetingIds={preparingMeetingIds}
              followingUpMeetingIds={followingUpMeetingIds}
            />
          ))}
        </div>
      );
    })}
  </div>
);

export default MeetingsListTable;
